/**
 * SubmitRequestForm Component
 *
 * Simple, user-friendly form for submitting feature requests, bug reports,
 * and change requests. Designed to be completed in < 2 minutes.
 *
 * @module components/feedback/SubmitRequestForm
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useUser } from '../../contexts/AuthContext';
import { FeatureRequestService } from '../../services/featureRequestService';
import ScreenshotUpload from './ScreenshotUpload';
import {
  RequestFormData,
  REQUEST_TYPES,
  USER_PRIORITIES,
  VALIDATION_RULES,
} from '../../types/featureRequest';

/**
 * SubmitRequestForm - User-facing form for submitting requests
 */
const SubmitRequestForm: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<RequestFormData>({
    type: 'feature',
    title: '',
    description: '',
    userPriority: 'medium',
    email: user?.email || '',
    screenshots: [],
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  /**
   * Handle form field changes
   */
  const handleChange = (field: keyof RequestFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  /**
   * Handle screenshot changes
   */
  const handleScreenshotsChange = (files: File[]) => {
    setFormData(prev => ({
      ...prev,
      screenshots: files,
    }));
  };

  /**
   * Validate form
   */
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Title validation
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.trim().length < VALIDATION_RULES.title.minLength) {
      errors.title = `Title must be at least ${VALIDATION_RULES.title.minLength} characters`;
    } else if (formData.title.length > VALIDATION_RULES.title.maxLength) {
      errors.title = `Title must be at most ${VALIDATION_RULES.title.maxLength} characters`;
    }

    // Description validation
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    } else if (formData.description.trim().length < VALIDATION_RULES.description.minLength) {
      errors.description = `Description must be at least ${VALIDATION_RULES.description.minLength} characters`;
    } else if (formData.description.length > VALIDATION_RULES.description.maxLength) {
      errors.description = `Description must be at most ${VALIDATION_RULES.description.maxLength} characters`;
    }

    // Email validation (if provided)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate form
    if (!validateForm()) {
      setError('Please fix the errors above before submitting');
      return;
    }

    setLoading(true);

    try {
      // Submit request
      await FeatureRequestService.createRequest(
        formData,
        user?.uid,
        user?.displayName || undefined
      );

      // Show success message
      setSuccess(true);

      // Redirect to My Requests after 2 seconds
      setTimeout(() => {
        navigate('/feedback/my-requests');
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting request:', err);
      setError(err.message || 'Failed to submit request. Please try again.');
      setLoading(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    navigate(-1);
  };

  // Show success message
  if (success) {
    return (
      <Box maxWidth={800} mx="auto" p={3}>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Thanks! We got it. 🚀
          </Typography>
          <Typography variant="body2">
            Your request has been submitted successfully. We'll review it and get back to you soon.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Redirecting to your requests...
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box maxWidth={800} mx="auto" p={3}>
      <Paper elevation={2} sx={{ p: 4 }}>
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h4" gutterBottom>
            📝 Request a Feature or Report a Bug
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We'd love to hear your feedback! This form should take less than 2 minutes to complete.
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Request Type */}
            <TextField
              select
              label="What type of request is this?"
              value={formData.type}
              onChange={handleChange('type')}
              required
              fullWidth
              helperText="Select the category that best describes your request"
            >
              {Object.entries(REQUEST_TYPES).map(([value, config]) => (
                <MenuItem key={value} value={value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>{config.icon}</span>
                    <Box>
                      <Typography variant="body1">{config.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {config.description}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            {/* Title */}
            <TextField
              label="Give it a short title"
              value={formData.title}
              onChange={handleChange('title')}
              required
              fullWidth
              error={!!validationErrors.title}
              helperText={
                validationErrors.title ||
                `Briefly describe what you want (${formData.title.length}/${VALIDATION_RULES.title.maxLength} characters)`
              }
              placeholder="e.g., Add dark mode to the application"
            />

            {/* Description */}
            <TextField
              label="Describe what you want"
              value={formData.description}
              onChange={handleChange('description')}
              required
              fullWidth
              multiline
              rows={5}
              error={!!validationErrors.description}
              helperText={
                validationErrors.description ||
                `Be as detailed as possible. What do you want to happen? Why? (${formData.description.length}/${VALIDATION_RULES.description.maxLength} characters)`
              }
              placeholder="Explain your request in detail. The more information you provide, the better we can help!"
            />

            <Divider />

            {/* Screenshots */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                📸 Add screenshots (optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Screenshots help us understand exactly what you mean
              </Typography>
              <ScreenshotUpload
                screenshots={formData.screenshots || []}
                onChange={handleScreenshotsChange}
                disabled={loading}
              />
            </Box>

            <Divider />

            {/* Priority */}
            <TextField
              select
              label="How important is this to you?"
              value={formData.userPriority}
              onChange={handleChange('userPriority')}
              required
              fullWidth
              helperText="Help us understand your needs"
            >
              {Object.entries(USER_PRIORITIES).map(([value, config]) => (
                <MenuItem key={value} value={value}>
                  <Box>
                    <Typography variant="body1">{config.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {config.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            {/* Email */}
            <TextField
              label="✉️ Email for updates (optional)"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              fullWidth
              error={!!validationErrors.email}
              helperText={
                validationErrors.email ||
                "We'll notify you when your request is reviewed or completed"
              }
              placeholder="your.email@example.com"
              disabled={!!user?.email} // Disabled if user is logged in
            />

            {/* Action Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
                size="large"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {loading ? 'Submitting...' : 'Submit Request 🚀'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>

      {/* Help Text */}
      <Box mt={3}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Tip:</strong> The more details you provide, the better we can understand and
            implement your request. Screenshots are especially helpful!
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default SubmitRequestForm;
